import { namedIn, shownId, storedId } from './partsCatalogue';
import { sideLabel, type PartItem, type Side } from './partsRun';
import type { PieceName } from './stepsRun';

// WHICH OF A MIRROR PAIR, IN THE BRACKET WITH THE CALLOUT — "(C, Left)".
//
// Inside the same bracket the letter is in, and not appended after it, because it is the same
// kind of fact: the bracket is how this app says WHICH thing is meant, and a pair's side is
// the second half of that answer. "Side rail (C) Left" reads as a rail placed leftwards; "Side
// rail (C, Left)" reads as the left one of the two, which is what it is.
//
// Its own exported helper because three readers put the same bracket together — the sentence
// below, the chips, and the viewer's own title — and three spellings of one bracket is how a
// reader stops trusting that they mean the same thing. Nothing when there is no side, which
// is nearly every part: an empty bracket half is not a fact about a screw.
export const sideWord = (side: Side | null | undefined, language: string): string =>
  (side ? `, ${sideLabel(side, language)}` : '');

// A SUB-STEP'S SENTENCE, WRITTEN OUT OF ITS OWN PARTS LIST.
//
// The model answers a gesture twice over: once as prose, once as a list of ids with
// quantities and roles. They described the same thing and nothing tied them together, so they
// drifted — and drifted silently, because a reader checks the sentence against the diagram
// and the columns against the sentence, and both look right on their own. 1F of one manual
// said "Attach the Side rail with barrel nuts, adjusted (105 x1) to the backrest and rear legs
// frame (A x1) by inserting a Prepared screw (101 x1)" over a parts list naming only the
// frame: two of the three pieces in the sentence were in no column, spent nothing, and
// nothing anywhere said so.
//
// SO THE SENTENCE IS NOT PROSE ABOUT THE PARTS, IT IS A TEMPLATE OVER THEM. The model writes
// an id in braces where a part belongs — "Attach {8} to {0} by inserting {101}." — and this
// puts the name, the callout and the count in as it is drawn, from the same list the chips and
// the availability column read. A part cannot be named in the sentence without being in the
// list, because there would be nothing to render.
//
// WHICH WAY ROUND IT DEPENDS IS THE POINT. The prose was the accurate half — it is where the
// model is actually describing the gesture — and the list was bookkeeping done alongside,
// which is why the list is the half that dropped a piece. Made of tokens, the sentence cannot
// be written until the list exists, so the half that was right now needs the half that was
// wrong. It is one inference where there were two.
//
// SENTENCES WRITTEN BEFORE THIS carry no braces and pass through untouched — nothing here
// matches, so they are returned as they were. They keep their old drift; nothing can undo a
// disagreement between two things authored separately.

// A token is "{" an id "}" — the ids being numbers and the app's own keys, never braces.
const TOKEN = /\{([^{}]+)\}/g;

// THE FIRST LETTER LOWERED, unless the second is a capital too: "JCBC screw" is an acronym,
// not a word that begins a sentence.
const midSentence = (name: string): string =>
  (name && !/^.\p{Lu}/u.test(name) ? name[0].toLowerCase() + name.slice(1) : name);

// The name a token stands for, as the sentence reads it: the thing, then what identifies it
// on the diagram and how many of it this one action takes — "spring washer (F ×1)",
// "Prepared screw (101 ×2)". The chips put the id first and the count last; a sentence wants
// the noun first, which is the difference between a label and a phrase.
// A side word written right after a part's token, or right before it.
const SIDE_AFTER = /^\s+(?:de\s+)?(?:droite|gauche|right|left)\b/i;
const SIDE_BEFORE = /\b(?:droite|gauche|right|left)(?:-hand)?\s+$/i;

// A NAME IN THE PLURAL, for a token whose entry takes more than one: "spring washers (F ×2)",
// "rondelles élastiques (F ×2)" — the count in the bracket was news the noun before it denied.
//
// By rule, not by dictionary. English: the last word of the noun, before any "of"/"with"/"for".
// French: every word up to the first preposition, bar a word already ending in s, x or z, a
// capitalised word after the first (a name, an acronym: "vis JCBC", "clé Allen"), and the front of
// a hyphenated word ("cache-trous"). A word the rule gets wrong is a misspelt plural, which reads
// better than a singular with "×2" after it.
const EN_STOP = new Set(['of', 'with', 'for', 'in', 'on']);
const FR_STOP = new Set(['de', 'du', 'des', 'd', 'en', 'à', 'au', 'aux', 'pour', 'avec', 'et', 'sur']);
const enWord = (w: string) => (/(?:s|x|z|ch|sh)$/i.test(w) ? `${w}es`
  : /[^aeiou]y$/i.test(w) ? `${w.slice(0, -1)}ies` : `${w}s`);
const frWord = (w: string) => {
  if (/[sxz]$/i.test(w)) return w;
  if (/(?:eau|eu|au)$/i.test(w)) return `${w}x`;
  if (/al$/i.test(w)) return `${w.slice(0, -2)}aux`;
  return `${w}s`;
};
export function pluralName(name: string, language: string): string {
  const words = name.split(' ');
  if (language === 'fr') {
    const stop = words.findIndex((w, i) => i > 0 && FR_STOP.has(w.toLowerCase().replace(/['’].*$/, '')));
    return words.map((w, i) => {
      if ((stop >= 0 && i >= stop) || (i > 0 && /^\p{Lu}/u.test(w)) || !/\p{L}/u.test(w)) return w;
      const parts = w.split('-');
      parts[parts.length - 1] = frWord(parts[parts.length - 1]);
      return parts.join('-');
    }).join(' ');
  }
  const stop = words.findIndex((w, i) => i > 0 && EN_STOP.has(w.toLowerCase()));
  const head = (stop >= 0 ? stop : words.length) - 1;
  return words.map((w, i) => (i === head && /\p{L}$/u.test(w) ? enWord(w) : w)).join(' ');
}

// AND ITS ARTICLE, in French, where the sentence wrote the singular one before it: "la {5}" of
// two washers is "les {5}", "du {0}" "des {0}", "à la {5}" "aux {5}". English "the" is both.
const frArticles = (text: string, many: (id: string) => boolean): string => text
  .replace(/\b(?:à la|à l['’]|au) ?\{([^{}]+)\}/gi, (all, id) => (many(id) ? `aux {${id}}` : all))
  .replace(/\b(?:de la|de l['’]|du) ?\{([^{}]+)\}/gi, (all, id) => (many(id) ? `des {${id}}` : all))
  .replace(/\b(?:la|le|l['’]) ?\{([^{}]+)\}/gi, (all, id) => (many(id) ? `les {${id}}` : all));

export function renderSubStepText(
  text: string,
  // The gesture's own parts, for the count. A token whose id is in no entry still renders —
  // the name is the useful half — it simply says nothing about how many.
  //
  // AND FOR THE SIDE, which is the other thing only the entry knows: the part is both hands
  // of a pair and the entry says which one this gesture holds. See UsedPart.side.
  parts: { id: string; qty?: number | null; side?: Side }[] | undefined,
  items: PartItem[],
  // The pieces earlier sub-steps assembled, by id. See pieceNames, which builds it from the
  // one place that numbers them.
  pieces: Map<string, PieceName> | undefined,
  language: string,
): string {
  const many = (id: string) => ((parts ?? []).find((p) => p.id === id.trim())?.qty ?? 1) > 1;
  const said = language === 'fr' ? frArticles(text || '', many) : (text || '');
  return said.replace(TOKEN, (whole: string, raw, at: number) => {
    const id = String(raw).trim();
    // THE SIDE ONCE: where the sentence already says it beside the part — "la {2} droite", "the
    // left {2}" — the name does not say it again ("traverse latérale (C, Droite) droite").
    const sided = SIDE_AFTER.test(said.slice(at + whole.length)) || SIDE_BEFORE.test(said.slice(0, at));
    // A NAME MID-SENTENCE IS A COMMON NOUN: "la vis JCBC", not "la Vis JCBC". The catalogue
    // capitalises names because a label stands alone; first in the sentence, it keeps that.
    const entry = (parts ?? []).find((p) => p.id === id);
    const inline = (name: string) => {
      const one = at > 0 ? midSentence(name) : name;
      return (entry?.qty ?? 1) > 1 ? pluralName(one, language) : one;
    };
    // "×1" is left off entirely rather than written out: a sentence that says "the seat (D ×1)"
    // of every single part reads as arithmetic, and one is what a gesture takes unless it says
    // otherwise. The count appears where it is news.
    const qty = entry?.qty ?? 1;
    // Against the letter, "(G×2)": a space there split one bracket into two facts. After a side
    // word, which is a word, the space stays: "(C, Left ×2)".
    const count = qty > 1 ? `×${qty}` : '';
    // The letter is the manual's own callout and is what a reader looks for on the diagram;
    // a part printed without one falls back to the number it is listed under.
    const asPart = (part: PartItem, partId: string, side: Side | undefined) => {
      const name = inline(namedIn(part, language)) || `#${partId}`;
      // A TOOL IS NAMED BY ITS LETTER OR NOT AT ALL: one the manual never lettered is "the
      // screwdriver", and the number it is listed under here is in no manual (see shownId).
      if (part.tool) return part.letter ? `${name} (${part.letter})` : name;
      const hand = sided ? '' : sideWord(side, language);
      return `${name} (${part.letter || shownId(partId)}${hand}${hand && count ? ' ' : ''}${count})`;
    };
    const part = items[Number(id)];
    if (part) return asPart(part, id, entry?.side);
    // A PIECE IS SAID AS ITS BASE PART — "the side rail (C, Right)", which is what the reader
    // has in hand, and not the name of everything done to it so far (see pieceBases).
    const piece = pieces?.get(id);
    const base = piece?.base && items[Number(piece.base.id)];
    if (piece?.base && base) return asPart(base, piece.base.id, piece.base.side ?? entry?.side);
    if (piece) return `${inline(piece.name)} (${id}${count})`;
    // AN ID NOTHING CAN RESOLVE, which is what a re-extracted parts list leaves behind. The
    // id alone, as every other reader of an unknown id in this app says it — and not the
    // braces, which would put a piece of this app's notation in front of somebody reading a
    // manual.
    return `#${id}${count}`;
  });
}

// THE IDS IN A SENTENCE COME BACK ONE HIGHER THAN THEY ARE STORED, exactly as the ids in
// "parts" do — they were offered that way (see shownId), and the answer quotes what it was
// offered. Put back at the moment the answer lands, so the tokens and the entries they render
// from are the same ids, and everything downstream goes on counting from zero.
export const storedTokens = (text: string | undefined, limit: number): string =>
  (text || '').replace(TOKEN, (_w, raw) => `{${storedId(String(raw).trim(), limit)}}`);
